import {
  DrawerDescription,
  DrawerContent,
  DrawerTrigger,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
  Separator,
  Loading,
  Button,
  Drawer,
  Form,
} from '@ui/components';
import { CostCenterFormType } from '@pages/cost-centers/utils';
import { PropsWithChildren, useState, FC } from 'react';
import { Cancel, Plus } from '@ui/components/icons';
import { HasPermission } from '@shared/components';
import { useTranslation } from 'react-i18next';

import { CostCenterDocumentsForm } from '../cost-center-documents-form/cost-center-documets-form';
import { CostCenterSettingsForm } from '../cost-center-settings-form/cost-center-settings-form';
import { CostCenterAssignForm } from '../cost-center-assign-form/cost-center-assign-form';
import { useEditCostCenterDrawer } from './use-edit-cost-center-drawer';
import { CostCenterForm } from '../cost-center-form/cost-center-form';

export const EditCostCenterDrawer: FC<PropsWithChildren<{ id?: string }>> = ({ id, children }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const {
    onSubmit,
    form: editForm,
    isPending,
    isCostCenterLoading,
  } = useEditCostCenterDrawer({
    onSuccess: () => {
      setIsOpen(false);
      form.reset();
    },
    id,
  });

  if (!id) {
    return null;
  }

  const form = editForm as CostCenterFormType;

  return (
    <Drawer
      onClose={() => {
        form.reset();
      }}
      onOpenChange={setIsOpen}
      open={isOpen}
      handleOnly
    >
      <DrawerTrigger asChild>
        {children || (
          <Button type="button">
            <Plus />
            {t('COST_CENTERS.EDIT_COST_CENTER')}
          </Button>
        )}
      </DrawerTrigger>
      <DrawerContent>
        <DrawerHeader className="flex">
          <DrawerTitle>
            <DrawerDescription>{t('COST_CENTERS.EDIT_COST_CENTER_DRAWER_TITLE')}</DrawerDescription>
          </DrawerTitle>
          <div className="flex ml-auto">
            <DrawerClose asChild>
              <Button variant="ghost" type="button">
                <Cancel />
              </Button>
            </DrawerClose>
          </div>
        </DrawerHeader>
        <div className="overflow-y-auto -my-4">
          <Loading isLoading={isCostCenterLoading}>
            <Form {...form}>
              <form className="px-4 space-y-4 pt-4">
                <CostCenterForm form={form} editMode />
                <Separator />
                <CostCenterSettingsForm costCenterId={id} form={form} editMode />
                <Separator />
                <CostCenterAssignForm form={form} editMode />
                <Separator />
                <CostCenterDocumentsForm form={form} editMode />
              </form>
            </Form>
          </Loading>
        </div>
        <DrawerFooter>
          <HasPermission
            permissionFeature="COST_CENTER_EDIT_COST_CENTER_ADDRESS"
            permissionType="WRITE"
          >
            <Button isLoading={isPending} disabled={isPending} onClick={onSubmit} size="large" wide>
              {t('COST_CENTERS.EDIT_COST_CENTER_DRAWER_SAVE')}
            </Button>
          </HasPermission>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
